import { Fragment } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from '@/components/ui/combobox';
import { renderLogo } from 'src/utils/renderLogo';
import { DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const getMigrationConnectSchema = (showRepositoryField: boolean) =>
  z.object({
    integrationId: z.string().min(1, 'Please select an integration'),
    oauthRepoId: showRepositoryField ? z.string().min(1, 'Please select a repository') : z.string().optional(),
  });

export type IMigrationConnectForm = {
  integrationId: string;
  oauthRepoId?: string;
};

const formContent = {
  integration: {
    title: 'Migrate assets to an OAuth integration',
    description:
      "Select the OAuth integration you want to migrate these assets to. If the integration you need isn't listed, add a new one on the Integrations page.",
    buttonText: 'Migrate',
  },
  repository: {
    title: 'Connect your asset to a repository',
    description:
      'Select the repository this asset belongs to. Only repositories connected to your OAuth integration will be listed.',
    buttonText: 'Connect',
  },
};

type Props = {
  integrationOptions: {
    value: string;
    provider: string;
    label: string;
  }[];
  repositoryOptions: {
    value: string;
    label: string;
  }[];
  onSubmit: (data: IMigrationConnectForm) => void;
  showRepositoryField?: boolean;
  onCancel: () => void;
};

const MigrationConnectForm = ({
  integrationOptions,
  repositoryOptions,
  onSubmit,
  showRepositoryField = false,
  onCancel,
}: Props) => {
  const form = useForm<IMigrationConnectForm>({
    resolver: zodResolver(getMigrationConnectSchema(showRepositoryField)),
    defaultValues: {
      integrationId: '',
      oauthRepoId: '',
    },
    mode: 'onChange',
  });

  const { buttonText, title, description } = formContent[showRepositoryField ? 'repository' : 'integration'];

  return (
    <Fragment>
      <DialogHeader className="gap-1.5">
        <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {!showRepositoryField && (
          <Controller
            control={form.control}
            name="integrationId"
            render={({ field }) => (
              <Combobox
                items={integrationOptions}
                itemToStringValue={(integration: { value: string; label: string }) => integration.label}
                onValueChange={(integration) => field.onChange(integration?.value)}
              >
                <ComboboxInput placeholder="Select integration" />
                <ComboboxContent>
                  <ComboboxGroup className="p-1">
                    <ComboboxLabel>Active integrations</ComboboxLabel>
                    <ComboboxList>
                      {(integration) => {
                        const Logo = renderLogo(integration.provider);

                        return (
                          <ComboboxItem key={integration.value} value={integration} className="py-1.5">
                            {Logo && <Logo className="size-5" />}
                            {integration.label}
                          </ComboboxItem>
                        );
                      }}
                    </ComboboxList>
                  </ComboboxGroup>
                  <ComboboxEmpty>No items found.</ComboboxEmpty>
                </ComboboxContent>
              </Combobox>
            )}
          />
        )}

        {showRepositoryField && (
          <Controller
            control={form.control}
            name="oauthRepoId"
            render={({ field }) => (
              <Combobox
                items={repositoryOptions}
                itemToStringValue={(integration: { value: string; label: string }) => integration.label}
                onValueChange={(integration) => field.onChange(integration?.value)}
              >
                <ComboboxInput placeholder="Select repository" />
                <ComboboxContent>
                  <ComboboxGroup className="p-1">
                    <ComboboxLabel>Repositories</ComboboxLabel>
                    <ComboboxList>
                      {(repository) => (
                        <ComboboxItem key={repository.value} value={repository} className="py-1.5">
                          {repository.label}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxGroup>
                  <ComboboxEmpty>No items found.</ComboboxEmpty>
                </ComboboxContent>
              </Combobox>
            )}
          />
        )}
        <DialogFooter className="sm:justify-between">
          <DialogClose asChild>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            disabled={!form.formState.isValid || form.formState.isSubmitting}
            isLoading={form.formState.isSubmitting}
          >
            {buttonText}
          </Button>
        </DialogFooter>
      </form>
    </Fragment>
  );
};

MigrationConnectForm.displayName = 'MigrationConnectForm';

export default MigrationConnectForm;
